'use es6';

import { Component } from 'react';
import PropTypes from 'prop-types';
import { DragSource } from 'react-dnd-compat';
import styled from 'foundations-theming/utils';
import { Z_INDEX_MAX_LAYERS, FLEX_COLUMN_RGB_COLORS } from 'InpageEditorUI/lib/layoutSection/constants';
import { MoveIcon } from 'foundations-assets/icons/components/MoveIcon';
import { jsx as _jsx } from "react/jsx-runtime";
const dragType = 'WIDGETS';
const dragSpec = {
  beginDrag(props) {
    const {
      startDraggingFromFlexColumn,
      widgetName,
      containerName,
      widgetElement,
      dragIndex
    } = props;
    // If the callback for a dragStart takes too long (because of DOM
    // manipulations and such, dragEnd can be called immediately. A
    // setTimeout defers the DOM manipulations to the next tick.
    // https://bugs.chromium.org/p/chromium/issues/detail?id=801071
    setTimeout(() => startDraggingFromFlexColumn({
      containerName,
      dragIndex
    }), 0);
    return {
      widgetName,
      containerName,
      widgetElement
    };
  },
  endDrag(props, monitor) {
    const {
      updateFlexColumnContents,
      endDraggingFromFlexColumn
    } = props;
    endDraggingFromFlexColumn();
    if (!monitor.didDrop()) {
      return;
    }
    const {
      widgetName: fromWidgetName,
      widgetElement: fromWidgetElement,
      containerName: fromContainerName
    } = monitor.getItem();
    const {
      dropIndex,
      flexDropColumnId
    } = monitor.getDropResult();

    // If we are not on a valid target, don't attempt to update the flex columns data
    if (dropIndex === null || flexDropColumnId === null) {
      return;
    }
    updateFlexColumnContents(fromWidgetName, fromContainerName, fromWidgetElement, flexDropColumnId, dropIndex);
  }
};
const dragCollect = monitor => ({
  isDragging: monitor.isDragging()
});
const DragOverlay = styled.div.withConfig({
  displayName: "FlexColumnDragAffordance__DragOverlay",
  componentId: "sc-1muo5wt-0"
})(({
  theme
}) => ({
  position: 'absolute',
  backgroundColor: FLEX_COLUMN_RGB_COLORS.DRAG_OVERLAY_BACKGROUND,
  zIndex: Z_INDEX_MAX_LAYERS.LEVEL_1,
  blockSize: '100%',
  inlineSize: '100%',
  '&:hover': {
    cursor: 'move',
    outline: `1px dashed ${FLEX_COLUMN_RGB_COLORS.DRAG_OVERLAY_OUTLINE}`,
    outlineOffset: '2px',
    '&:before': {
      content: '""',
      position: 'absolute',
      background: 'none',
      borderWidth: theme.borderWidth[100],
      borderStyle: 'dashed',
      borderColor: FLEX_COLUMN_RGB_COLORS.DRAG_SURFACE_BACKGROUND,
      display: 'block',
      insetBlockStart: -4,
      insetInlineStart: -4,
      insetBlockEnd: -4,
      insetInlineEnd: -4,
      pointerEvents: 'none'
    }
  }
}));
const IconWrapper = styled.div.withConfig({
  displayName: "FlexColumnDragAffordance__IconWrapper",
  componentId: "sc-1muo5wt-1"
})(({
  theme,
  $iconBadgeScale,
  $marginOffset
}) => ({
  boxSizing: 'content-box',
  fontSize: `${$iconBadgeScale / 2}px`,
  color: `${FLEX_COLUMN_RGB_COLORS.DRAG_OVERLAY_FONT}`,
  height: `${$iconBadgeScale}px`,
  width: `${$iconBadgeScale}px`,
  borderRadius: `${$iconBadgeScale}px`,
  marginTop: `${$marginOffset}px`,
  marginInline: 'auto',
  borderWidth: theme.space[50],
  borderStyle: 'solid',
  borderColor: `${FLEX_COLUMN_RGB_COLORS.DRAG_OVERLAY_OUTLINE}`,
  backgroundColor: FLEX_COLUMN_RGB_COLORS.DRAG_SURFACE_BACKGROUND,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));
class FlexColumnDragAffordance extends Component {
  render() {
    const {
      connectDragSource,
      widgetElement
    } = this.props;
    const height = widgetElement.offsetHeight;
    const iconBadgeScale = height <= 100 ? height : 100;
    const marginOffset = iconBadgeScale === 100 ? `${height / 2 - iconBadgeScale / 2}` : 0;
    return connectDragSource( /*#__PURE__*/_jsx("div", {
      "data-hs-dragdrop": true,
      children: /*#__PURE__*/_jsx(DragOverlay, {
        id: `${widgetElement.id}_overlay`,
        "data-test-id": "flex-column-drag-affordance",
        children: /*#__PURE__*/_jsx(IconWrapper, {
          $iconBadgeScale: iconBadgeScale,
          $marginOffset: marginOffset,
          children: /*#__PURE__*/_jsx(MoveIcon, {})
        })
      })
    }));
  }
}
FlexColumnDragAffordance.propTypes = {
  connectDragSource: PropTypes.func.isRequired,
  containerName: PropTypes.string.isRequired,
  // eslint-disable-line react/no-unused-prop-types
  dragIndex: PropTypes.number.isRequired,
  // eslint-disable-line react/no-unused-prop-types
  endDraggingFromFlexColumn: PropTypes.func.isRequired,
  // eslint-disable-line react/no-unused-prop-types
  startDraggingFromFlexColumn: PropTypes.func.isRequired,
  // eslint-disable-line react/no-unused-prop-types
  updateFlexColumnContents: PropTypes.func.isRequired,
  // eslint-disable-line react/no-unused-prop-types
  widgetElement: PropTypes.object.isRequired,
  widgetName: PropTypes.string.isRequired // eslint-disable-line react/no-unused-prop-types
};
export default DragSource(dragType, dragSpec, dragCollect)(FlexColumnDragAffordance);